// app/src/context/BillingContext.tsx
//
// Fetches and provides the current org's subscription status.
// Used by ProtectedRoute to gate access, and by BillingPage / SettingsPage
// to show billing UI.

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/apiClient';

export type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | null;

interface BillingState {
  subscriptionStatus: SubscriptionStatus;
  pilotExpiresAt: string | null;   // ISO timestamp; non-null only for pilot orgs
  hasSubscription: boolean;
  isLoading: boolean;
  isActive: boolean;               // true if status='active' and pilot not expired
  refetch: () => void;
}

const BillingContext = createContext<BillingState | undefined>(undefined);

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { session, orgId, loading: authLoading } = useAuth();

  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>(null);
  const [pilotExpiresAt, setPilotExpiresAt] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchTick, setFetchTick] = useState(0);

  const refetch = useCallback(() => setFetchTick((n) => n + 1), []);

  useEffect(() => {
    if (!session) {
      // Don't clear loading while auth is still resolving — doing so causes a
      // brief render where authLoading=false, billingLoading=false, isActive=false,
      // which incorrectly redirects to /billing before the billing fetch even starts.
      if (!authLoading) {
        setIsLoading(false);
      }
      return;
    }

    if (!orgId) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    api
      .get<{
        ok: boolean;
        subscriptionStatus: SubscriptionStatus;
        pilotExpiresAt: string | null;
        hasSubscription: boolean;
      }>('/billing/status')
      .then((data) => {
        if (!isMounted) return;
        setSubscriptionStatus(data.subscriptionStatus);
        setPilotExpiresAt(data.pilotExpiresAt);
        setHasSubscription(data.hasSubscription);
      })
      .catch(() => {
        // On error (including 402 if somehow hit), keep defaults (null / false)
        if (!isMounted) return;
        setSubscriptionStatus(null);
        setPilotExpiresAt(null);
        setHasSubscription(false);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [session, orgId, fetchTick, authLoading]);

  const isActive =
    subscriptionStatus === 'active' &&
    (!pilotExpiresAt || new Date(pilotExpiresAt) > new Date());

  const value: BillingState = {
    subscriptionStatus,
    pilotExpiresAt,
    hasSubscription,
    isLoading,
    isActive,
    refetch,
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
}
